import { DeviceCounter } from '@shared/models/device-info';
import { IReplicableEntity } from '@shared/models/replicable-entity';
import { Metadata } from '@shared/models/metadata';

/**
 * The counters the POS relies on for document codes. A plain string is also
 * accepted so a new series does not require a model change.
 */
export type DeviceCounterEntityType =
  | 'sales_order'
  | 'sales_order_v3'
  | 'sales_refund'
  | 'cash_register_closure'
  | (string & {});

/**
 * A counter as stored (Firestore document shape). A counter numbers one cash
 * register's documents, so it is keyed by tenant + register + entity type and
 * survives re-pairing. Consumers keep working with the wire
 * {@link DeviceCounter} shape; the transitions module converts between the two.
 */
export interface DeviceCounterEntity extends IReplicableEntity {
  cashRegisterId: string;
  entityType: DeviceCounterEntityType;
  current: number;
  lastResetAt: Date;
  createdAt: Date;
  metadata?: Metadata;
}

/** Every counter mutation the Native Service exposes. */
export type DeviceCounterOperation = 'increment' | 'decrement' | 'reset' | 'set';

/**
 * A change the store computed and wrote. Carries the resulting counter state,
 * never a delta, so replaying it is safe.
 */
export interface DeviceCounterStateChange {
  cashRegisterId: string;
  documentId: string;
  entityType: DeviceCounterEntityType;
  operation: DeviceCounterOperation;
  /** When the change was written — the ordering stamp. */
  occurredAt: Date;
  state: DeviceCounterEntity;
  correlationId: string;
  source: string;
  primaryOperation: string;
}

export type DeviceCounterSyncOutcome =
  | {
    /** The change sits in the durable local queue; `acknowledged` settles when the backend accepted or refused it. */
    status: 'applied';
    created: boolean;
    acknowledged: Promise<void>;
  }
  | { status: 'skipped'; reason: 'stale' | 'nothingToSync' | 'primaryFailed'; existingStamp?: Date }
  | { status: 'failed'; error: unknown };

/**
 * Result of an application-level counter mutation. `counter` is the resulting
 * value in the wire shape consumers already use — `undefined` when the counter
 * could not be read or written, the contract they already branch on.
 * `firestoreSync` never rejects: failures are logged to Seq and reported here.
 */
export interface DeviceCounterOperationResult {
  counter: DeviceCounter | undefined;
  firestoreSync: Promise<DeviceCounterSyncOutcome>;
  /** What the store wrote, carried so the facade can log one terminal Seq event per write. */
  change?: DeviceCounterStateChange;
}

/** Concurrency / provenance block stored on the document. */
export interface DeviceCounterRevision {
  changedAt?: Date;
  lastOperation?: DeviceCounterOperation;
  source?: string;
  correlationId?: string;
}

/**
 * The contract every counter store honours, implemented by
 * `DeviceCounterReplicationService` and selected through the
 * `DEVICE_COUNTER_STORE` token (see `device-counter-store.token.ts`).
 */
export interface DeviceCounterStore {
  /** The stored counter, or `undefined` when it does not exist or no register id was given. */
  getAsync(cashRegisterId: string, entityType: DeviceCounterEntityType): Promise<DeviceCounter | undefined>;
  incrementAsync(cashRegisterId: string, entityType: DeviceCounterEntityType): Promise<DeviceCounterOperationResult>;
  resetAsync(cashRegisterId: string, entityType: DeviceCounterEntityType): Promise<DeviceCounterOperationResult>;
  setAsync(cashRegisterId: string, entityType: DeviceCounterEntityType, value: number): Promise<DeviceCounterOperationResult>;
}

/** Firestore collection holding the latest value of every counter. */
export const DEVICE_COUNTERS_COLLECTION = 'deviceCounters';

/** Document id: one counter per tenant, cash register and entity type. */
export function counterDocumentId(organizationId: string, cashRegisterId: string, entityType: DeviceCounterEntityType): string {
  return `${organizationId}_${cashRegisterId}_${entityType}`;
}
